import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuickActionsPanel = ({ statistics, savedFilters, onSearch, onFilterApply }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');

  const handleSearch = (e) => {
    e?.preventDefault();
    onSearch(searchQuery);
  };

  const statCards = [
    {
      label: 'Aktive Klienten',
      value: statistics?.activeClients,
      icon: 'Users',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: 'Termine heute',
      value: statistics?.todayAppointments,
      icon: 'Calendar',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'Offene Aufgaben',
      value: statistics?.pendingTasks,
      icon: 'ListTodo',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      label: 'Dringende Fälle',
      value: statistics?.urgentCases,
      icon: 'AlertCircle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    }
  ];

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Zap" size={20} />
          Schnellzugriff
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
        {/* Quick Search */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Klientensuche</h3>
          <form onSubmit={handleSearch} className="space-y-2">
            <Input
              type="search"
              placeholder="ID, Name oder Telefon..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full"
            />
            <button
              type="submit"
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Icon name="Search" size={16} />
              Suchen
            </button>
          </form>
        </div>

        {/* Saved Filters */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Gespeicherte Filter</h3>
          <Select
            placeholder="Filter auswählen..."
            options={savedFilters?.map(filter => ({
              value: filter?.id,
              label: filter?.name,
              description: filter?.description
            }))}
            value={selectedFilter}
            onChange={(value) => {
              setSelectedFilter(value);
              onFilterApply(value);
            }}
          />
          <div className="space-y-2">
            {savedFilters?.slice(0, 4)?.map((filter) => (
              <button
                key={filter?.id}
                onClick={() => onFilterApply(filter?.id)}
                className="w-full p-2 text-left rounded-md border border-border hover:bg-muted transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="Filter" size={14} />
                    <span className="text-sm font-medium">{filter?.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{filter?.count}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Daily Statistics */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Tagesstatistik</h3>
          <div className="space-y-2">
            {statCards?.map((stat, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-lg ${stat?.bgColor} border border-border`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={stat?.icon} size={18} className={stat?.color} />
                    <span className="text-sm font-medium text-foreground">{stat?.label}</span>
                  </div>
                  <span className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Schnellaktionen</h3>
          <div className="space-y-2">
            <button className="w-full p-3 text-left rounded-md border border-border hover:bg-muted transition-colors flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                <Icon name="UserPlus" size={16} className="text-primary" />
              </div>
              <span className="text-sm font-medium">Neuer Klient</span>
            </button>
            <button className="w-full p-3 text-left rounded-md border border-border hover:bg-muted transition-colors flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-success/10 flex items-center justify-center">
                <Icon name="CalendarPlus" size={16} className="text-success" />
              </div>
              <span className="text-sm font-medium">Termin erstellen</span>
            </button>
            <button className="w-full p-3 text-left rounded-md border border-border hover:bg-muted transition-colors flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-warning/10 flex items-center justify-center">
                <Icon name="FileText" size={16} className="text-warning" />
              </div>
              <span className="text-sm font-medium">Dokument generieren</span>
            </button>
            <button className="w-full p-3 text-left rounded-md border border-border hover:bg-muted transition-colors flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-accent/10 flex items-center justify-center">
                <Icon name="Download" size={16} className="text-accent" />
              </div>
              <span className="text-sm font-medium">Bericht exportieren</span>
            </button>
          </div>
        </div>

        {/* System Status */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Systemstatus</h3>
          <div className="space-y-2">
            <div className="p-2 rounded-md bg-success/10 border border-success/20">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-success" />
                <span className="text-xs font-medium text-success">Sozialversicherung: Verbunden</span>
              </div>
            </div>
            <div className="p-2 rounded-md bg-success/10 border border-success/20">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-success" />
                <span className="text-xs font-medium text-success">Berichtssystem: Online</span>
              </div>
            </div>
            <div className="p-2 rounded-md bg-muted border border-border">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Letzte Sync: vor 5 Min.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Tastenkombinationen</h3>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between p-2 rounded bg-muted">
              <span className="text-muted-foreground">Aufgaben</span>
              <kbd className="px-2 py-1 bg-background border border-border rounded font-mono">Strg+1</kbd>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-muted">
              <span className="text-muted-foreground">Interaktionen</span>
              <kbd className="px-2 py-1 bg-background border border-border rounded font-mono">Strg+2</kbd>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-muted">
              <span className="text-muted-foreground">Schnellzugriff</span>
              <kbd className="px-2 py-1 bg-background border border-border rounded font-mono">Strg+3</kbd>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-muted">
              <span className="text-muted-foreground">Suche</span>
              <kbd className="px-2 py-1 bg-background border border-border rounded font-mono">Strg+F</kbd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActionsPanel;